import { signOutUser } from "@/api/login";
import { globalQueryClient } from "@/lib/queryClient";
import { PROFILE_AVATAR_STORAGE_PREFIX } from "@/components/Profile/avatarUtils";
import { resetFeedState } from "@/feed/state/resetFeedState";
import { cancelAllPendingLearningWrites } from "@/feed/lib/wordStatus";

// localStorage keys that are NOT per-user state and must survive logout so we
// don't reset cross-session bookkeeping. Everything else is treated as
// per-user state and wiped (mirrors iOS signOutUser clearing the global store,
// persisted user flags, feed store, and AsyncStorage — see
// client-native/src/api/login.tsx:59-116).
const LOGOUT_PRESERVE_KEYS = new Set<string>([
  "__lastCleanup", // see @/lib/cleanup — weekly-cleanup bookkeeping, not user data
]);

// Per-user keys that are keyed BY user id and should survive logout so a user
// who signs back in keeps their chosen avatar / last course — iOS preserves
// exactly these prefixes (PROFILE_AVATAR_STORAGE_PREFIX + "currentCourse_") in
// signOutUser (client-native/src/api/login.tsx:91-95). They're namespaced by
// user id, so they can't leak between accounts.
const LOGOUT_PRESERVE_PREFIXES = [PROFILE_AVATAR_STORAGE_PREFIX, "currentCourse_"];

const shouldPreserveOnLogout = (key: string): boolean =>
  LOGOUT_PRESERVE_KEYS.has(key) ||
  LOGOUT_PRESERVE_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * Full logout for the web client. Mirrors iOS `signOutUser`, which on a real
 * logout clears auth + the global/feed stores + per-user persisted state so a
 * shared browser/device doesn't leak the previous user's data into the next
 * session.
 *
 * This is deliberately SEPARATE from `signOutUser()`: the web reuses
 * `signOutUser()` as a pre-auth "clear any temp users" step inside
 * `signInUser` / `signInWithProvider`, and the OAuth flow round-trips
 * `provider` / `isTemp` / `parrotUserId` through localStorage across a
 * full-page redirect (see RedirectPage.tsx). Sweeping localStorage inside
 * `signOutUser()` would destroy that in-flight auth state and break login, so
 * the heavy clear is gated to the actual logout path only.
 *
 * How to verify: log in, populate state (open a course, do a review), log out,
 * then in DevTools assert localStorage has only the preserved keys and
 * `globalQueryClient.getQueryCache().getAll()` is empty.
 */
export function logoutAndClearUserState(): void {
  // Clear auth (token, pb.authStore, PostHog reset).
  signOutUser();

  // Drop every cached server response so the next user starts clean.
  globalQueryClient.clear();

  // Reset the in-memory Legend feed stores (not covered by RQ/localStorage).
  resetFeedState();

  // Drop any deferred word-status writes so one can't fire after logout and
  // persist against the next account's token.
  cancelAllPendingLearningWrites();

  // Wipe per-user localStorage (review/feed/tutorial/onboarding/course/level
  // /bio/stats flags), preserving only non-user bookkeeping keys.
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !shouldPreserveOnLogout(key)) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch (err) {
    console.error("[logoutAndClearUserState] Error clearing localStorage:", err);
  }
}

/**
 * Account-DELETION cleanup. Unlike logout, this preserves NOTHING — the account
 * is gone, so the per-user avatar/course keys (which logout deliberately keeps
 * for re-login) must also be wiped, along with all other local state. The caller
 * has already invalidated the server (deleteUser → signOutUser clears the token)
 * and the react-query cache.
 */
export function clearAllUserStateForDeletion(): void {
  resetFeedState();
  cancelAllPendingLearningWrites();
  try {
    localStorage.clear();
  } catch (err) {
    console.error("[clearAllUserStateForDeletion] Error clearing localStorage:", err);
  }
}
